import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import Seo from "@/components/Seo";
import PageMasthead from "@/components/PageMasthead";
import { FadeUp } from "@/components/motion";
import useApi from "@/hooks/useApi";
import { BRAND_DETAILS } from "@/content/brands";

const WSA_FALLBACK = BRAND_DETAILS.whichschooladvisor.ad_guidelines;
const SC_FALLBACK = BRAND_DETAILS.schoolscompared.ad_guidelines;

/**
 * Unlisted page — not linked from the public /advertising view, excluded from
 * the sitemap, and marked noindex. Contains the full Jumbo profile sizing
 * tables for internal/partner use (special-booking creative only). Share the
 * direct URL with partners when a Jumbo upgrade is approved.
 *
 * URL: /advertising/reserved-sizes
 */
const ReservedSizes = () => {
  const { data: brands } = useApi("/brands");
  const list = brands || [];
  const byName = (n) => list.find((b) => b.name === n);
  const WSA = byName("WhichSchoolAdvisor")?.ad_guidelines || WSA_FALLBACK;
  const SC = byName("SchoolsCompared")?.ad_guidelines || SC_FALLBACK;

  return (
    <div data-testid="reserved-sizes-page">
      <Seo
        title="Special size, Reserved"
        path="/advertising/reserved-sizes"
        description="Internal reference — reserved Jumbo advertising sizes for special-booking creative on WhichSchoolAdvisor.com and SchoolsCompared.com."
        noindex
      />
      <PageMasthead
        kicker="Advertising · Reserved"
        titleLines={[{ text: "Special size," }, { text: "Reserved", accent: true }]}
        standfirst="This page is unlisted and shared by direct link only. The Jumbo profile is reserved for approved special-booking creative on WhichSchoolAdvisor.com and SchoolsCompared.com — not part of the standard media pack."
      />

      <section className="mx-auto max-w-[1400px] px-5 md:px-8 pb-6">
        <FadeUp>
          <div className="flex flex-wrap items-center gap-3 bg-ink/5 border border-border rounded-2xl p-5">
            <Lock size={18} className="text-primary shrink-0" />
            <p className="font-sans text-sm text-foreground/80">
              <span className="font-bold text-ink">Internal / partner only.</span>{" "}
              This URL is excluded from search engines and from the public media pack. If you've been sent this link,
              Jumbo creative may be available for your campaign — please contact your account manager to confirm
              inventory before producing creative at these dimensions.
            </p>
          </div>
        </FadeUp>
      </section>

      {[
        { brand: "WhichSchoolAdvisor.com", g: WSA, bg: "bg-background" },
        { brand: "SchoolsCompared.com", g: SC, bg: "bg-secondary" },
      ].map(({ brand, g, bg }) =>
        g.jumbo ? (
          <section key={brand} className={bg}>
            <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
              <FadeUp>
                <h2 className="headline text-3xl md:text-5xl uppercase text-ink max-w-3xl">
                  {brand} <span className="text-primary">· Jumbo</span>
                </h2>
                <p className="mt-4 max-w-3xl text-base text-foreground/70">
                  Tall creative at special premium dimensions, available by arrangement. All sizes are responsive;
                  display dimensions are 50% of the upload size once the minimum screen width is met.
                </p>
              </FadeUp>

              <FadeUp>
                <div className="mt-10 bg-white rounded-2xl border border-border overflow-hidden">
                  <div className="p-5 border-b border-border flex items-center justify-between gap-4">
                    <div>
                      <p className="headline text-xl text-ink">Jumbo Profile</p>
                      <p className="text-sm text-foreground/60">Reserved — special booking only</p>
                    </div>
                    <span className="font-mono text-[11px] font-extrabold uppercase tracking-widest text-primary border border-primary/40 rounded-full px-3 py-1">
                      Reserved
                    </span>
                  </div>
                  <table className="w-full text-left" data-testid={`reserved-jumbo-${brand}`}>
                    <thead>
                      <tr className="bg-secondary">
                        {["Size", "Upload (px)", "Display (px)", "Min. screen"].map((h) => (
                          <th key={h} className="px-4 py-3 font-sans text-[11px] font-extrabold uppercase tracking-wide text-foreground/50">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {g.jumbo.map((r) => (
                        <tr key={r.size} className="border-t border-border">
                          <td className="px-4 py-3 font-bold text-ink">{r.size}</td>
                          <td className="px-4 py-3 text-sm text-foreground/80">{r.upload}</td>
                          <td className="px-4 py-3 text-sm text-foreground/80">{r.display}</td>
                          <td className="px-4 py-3 text-sm text-foreground/80">{r.minScreen}px</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </FadeUp>
            </div>
          </section>
        ) : null,
      )}

      <section className="mx-auto max-w-[1400px] px-5 md:px-8 py-16">
        <Link
          to="/advertising"
          data-testid="reserved-back"
          className="inline-flex items-center gap-2 font-sans text-sm font-bold text-ink hover:text-primary transition-colors"
        >
          <ArrowLeft size={16} /> Back to the public media pack
        </Link>
      </section>
    </div>
  );
};

export default ReservedSizes;
